import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';

export interface MLComparisonData {
  avgLeadTimeLegacy: number;
  avgLeadTimeML: number;
  coverageLegacy: number;
  coverageML: number;
  improvement: number;
  totalSkuLocations: number;
}

@Component({
  selector: 'ml-comparison-chart',
  templateUrl: './ml-comparison-chart.component.html',
  styleUrls: ['./ml-comparison-chart.component.scss'],
  standalone: false
})
export class MLComparisonChartComponent implements OnInit, OnChanges {
  @Input() data: MLComparisonData | null = null;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  ngOnInit(): void {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.initializeChart();
    }
  }

  private initializeChart(): void {
    if (!this.data) {
      return;
    }

    const categories = ['Avg Lead Time (days)', 'Coverage (%)'];
    const legacyData = [this.data.avgLeadTimeLegacy, this.data.coverageLegacy];
    const mlData = [this.data.avgLeadTimeML, this.data.coverageML];

    this.chartOptions = {
      chart: {
        type: 'column',
        height: '320',
        width: 300,
        backgroundColor: '#ffffff',
        plotBorderWidth: 1,
        plotBorderColor: '#e0e5eb',
        marginTop: 40,
        marginBottom: 50,
        marginLeft: 80,
        marginRight: 15,
        spacingTop: 10,
        spacingRight: 10
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: categories,
        crosshair: true,
        labels: {
          style: {
            fontSize: '12px',
            color: '#29333d'
          }
        },
        lineColor: '#e0e5eb',
        lineWidth: 1,
        tickColor: '#e0e5eb',
        tickWidth: 1,
        tickLength: 5,
        opposite: false
      },
      yAxis: {
        title: {
          text: 'Value',
          align: 'middle',
          style: {
            color: '#29333d',
            fontSize: '12px',
            fontWeight: '600'
          }
        },
        labels: {
          enabled: true,
          style: {
            color: '#29333d',
            fontSize: '11px',
            fontWeight: '400'
          },
          formatter: function () {
            return Number(this.value).toFixed(1);
          }
        },
        gridLineColor: '#eff2f5',
        gridLineWidth: 1,
        lineColor: '#e0e5eb',
        lineWidth: 1,
        tickColor: '#29333d',
        tickWidth: 2,
        tickLength: 8,
        tickPosition: 'outside',
        showLastLabel: true
      },
      legend: {
        align: 'right',
        verticalAlign: 'top',
        floating: true,
        x: -10,
        y: 10,
        itemStyle: {
          fontSize: '12px',
          fontWeight: '400',
          color: '#29333d'
        },
        itemHoverStyle: {
          color: '#29333d'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 0,
        shadow: false
      },
      plotOptions: {
        column: {
          pointPadding: 0.05,
          borderWidth: 0,
          groupPadding: 0.1,
          pointWidth: 20,
          dataLabels: {
            enabled: false
          }
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        borderColor: '#e0e5eb',
        borderRadius: 8,
        shadow: false,
        useHTML: true,
        formatter: function () {
          const metricName = categories[this.x as number];
          const modelType = this.series.name;
          const value = this.y;

          let formattedValue = '';
          let unit = '';

          // Format based on metric type
          if (metricName === 'Avg Lead Time (days)') {
            formattedValue = value?.toFixed(1) || '0.0';
            unit = ' days';
          } else if (metricName === 'Coverage (%)') {
            formattedValue = value?.toFixed(1) || '0.0';
            unit = '%';
          }

          return `
            <div style="padding: 8px;">
              <div style="font-weight: 700; margin-bottom: 4px;">${metricName}</div>
              <div style="margin: 4px 0;">
                <span style="color: ${this.series.color};">●</span> 
                ${modelType}: <strong>${formattedValue}${unit}</strong>
              </div>
            </div>
          `;
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      series: [
        {
          name: 'Legacy',
          type: 'column',
          data: legacyData,
          color: '#5c7b1e'
        },
        {
          name: 'ML',
          type: 'column',
          data: mlData,
          color: '#7ba428'
        }
      ]
    };
  }

  get improvementValue(): string {
    if (!this.data) return '0.00';
    return this.data.improvement.toFixed(2);
  }

  get improvementClass(): string {
    if (!this.data) return 'neutral';
    return this.data.improvement >= 0 ? 'positive' : 'negative';
  }

  get improvementIcon(): string {
    if (!this.data) return '';
    return this.data.improvement >= 0 ? '▲' : '▼';
  }

  get totalSkuLocations(): string {
    if (!this.data) return '0';
    return this.data.totalSkuLocations.toLocaleString();
  }
}
